import React, { useEffect, useState, useCallback } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Alert, AlertDescription, AlertTitle } from "./ui/alert";
import { AlertTriangle, ShieldAlert, Loader2, Download, FileText } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";

/**
 * Modale bloquante — purge l'historique des demandes de sites (et affectations)
 * avec filtre par période optionnel.
 *
 * Props :
 *  - open, onOpenChange
 *  - onDone(deletedCounts) : callback après succès
 */
export default function PurgeSiteHistoryDialog({ open, onOpenChange, onDone }) {
  const [scope, setScope] = useState("range"); // "range" | "all"
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [confirmText, setConfirmText] = useState("");
  const [busy, setBusy] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exported, setExported] = useState(false); // export effectué au moins une fois cette session
  const [preview, setPreview] = useState({ loading: false, requests: null, assignments: null });

  useEffect(() => {
    if (open) {
      setScope("range");
      setStartDate("");
      setEndDate("");
      setConfirmText("");
      setBusy(false);
      setExporting(false);
      setExported(false);
      setPreview({ loading: false, requests: null, assignments: null });
    }
  }, [open]);

  // Comptage en direct
  const rangeParams = useCallback(() => {
    const p = {};
    if (scope === "range") {
      if (startDate) p.start_date = startDate;
      if (endDate) p.end_date = endDate;
    }
    return p;
  }, [scope, startDate, endDate]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      setPreview((p) => ({ ...p, loading: true }));
      try {
        const { data } = await api.get("/equipment-site-requests/purge-preview", {
          params: rangeParams(),
        });
        if (!cancelled) {
          setPreview({
            loading: false,
            requests: data.requests_count,
            assignments: data.assignments_count,
          });
        }
      } catch {
        if (!cancelled) setPreview({ loading: false, requests: null, assignments: null });
      }
    })();
    return () => { cancelled = true; };
  }, [open, rangeParams]);

  const isConfirmed = confirmText.trim().toUpperCase() === "PURGE";
  const rangeInvalid =
    scope === "range" &&
    startDate && endDate && startDate > endDate;
  const nothingToPurge =
    !preview.loading &&
    preview.requests === 0 && preview.assignments === 0;

  const downloadCsv = async () => {
    setExporting(true);
    try {
      const resp = await api.get("/equipment-site-requests/purge-export.csv", {
        params: rangeParams(),
        responseType: "blob",
      });
      const blob = new Blob([resp.data], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const stamp = new Date().toISOString().slice(0, 10);
      a.download = `demandes-sites-avant-purge_${stamp}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      setExported(true);
      toast.success("Export CSV téléchargé");
    } catch (e) {
      toast.error("Erreur lors de l'export CSV");
    } finally {
      setExporting(false);
    }
  };

  const submit = async () => {
    if (!isConfirmed) {
      toast.error("Tapez PURGE pour confirmer.");
      return;
    }
    if (rangeInvalid) {
      toast.error("La date de début doit être antérieure à la date de fin.");
      return;
    }
    setBusy(true);
    try {
      const params = { confirm: "PURGE" };
      if (scope === "range") {
        if (startDate) params.start_date = startDate;
        if (endDate) params.end_date = endDate;
      }
      const { data } = await api.post("/equipment-site-requests/purge-all", null, { params });
      toast.success(
        `Purge effectuée : ${data.deleted_requests} demande(s) + ${data.deleted_assignments} affectation(s) supprimée(s)`
      );
      onDone?.(data);
      onOpenChange?.(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors de la purge");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="purge-site-history-dialog">
        <DialogHeader>
          <DialogTitle className="inline-flex items-center gap-2 text-red-700">
            <ShieldAlert size={20} className="text-red-600" />
            Purger l&apos;historique des demandes de sites
          </DialogTitle>
          <DialogDescription>
            Cette action supprime définitivement les documents des collections
            <span className="font-mono text-xs mx-1">equipment_site_requests</span>
            et
            <span className="font-mono text-xs mx-1">equipment_site_assignments</span>.
            Elle est irréversible.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4" data-testid="purge-dialog-body">
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-wider text-zinc-700">Périmètre</Label>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setScope("range")}
                data-testid="purge-scope-range"
                className={`flex-1 px-3 py-2 border rounded-sm text-sm transition ${
                  scope === "range"
                    ? "bg-zinc-900 text-white border-zinc-900"
                    : "bg-white border-zinc-200 text-zinc-700 hover:bg-zinc-50"
                }`}
              >
                Filtrer par période
              </button>
              <button
                type="button"
                onClick={() => setScope("all")}
                data-testid="purge-scope-all"
                className={`flex-1 px-3 py-2 border rounded-sm text-sm transition ${
                  scope === "all"
                    ? "bg-red-600 text-white border-red-700"
                    : "bg-white border-zinc-200 text-zinc-700 hover:bg-zinc-50"
                }`}
              >
                Tout supprimer
              </button>
            </div>
          </div>

          {scope === "range" && (
            <div className="grid grid-cols-2 gap-3" data-testid="purge-range-fields">
              <div>
                <Label htmlFor="purge-start" className="text-xs">Date début</Label>
                <Input
                  id="purge-start"
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  data-testid="purge-start-date"
                />
              </div>
              <div>
                <Label htmlFor="purge-end" className="text-xs">Date fin (incluse)</Label>
                <Input
                  id="purge-end"
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  data-testid="purge-end-date"
                />
              </div>
              <p className="col-span-2 text-[11px] text-zinc-500 font-mono">
                Laissez un champ vide pour ne pas borner ce côté (ex. tout avant le 31/12).
              </p>
            </div>
          )}

          {/* Aperçu en direct + export CSV avant purge */}
          <div
            className="border border-zinc-200 rounded-sm p-3 bg-zinc-50 space-y-2"
            data-testid="purge-preview-card"
          >
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2 text-sm text-zinc-800">
                <FileText size={14} className="text-zinc-500" />
                <span className="font-medium">Aperçu de la purge</span>
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={downloadCsv}
                disabled={exporting || preview.loading || nothingToPurge}
                data-testid="purge-export-csv-btn"
                className="h-8"
              >
                {exporting ? (
                  <>
                    <Loader2 size={13} className="mr-1.5 animate-spin" /> Export…
                  </>
                ) : (
                  <>
                    <Download size={13} className="mr-1.5" />
                    Exporter CSV avant purge
                  </>
                )}
              </Button>
            </div>
            {preview.loading ? (
              <p className="text-xs text-zinc-500 font-mono inline-flex items-center gap-1.5">
                <Loader2 size={11} className="animate-spin" /> Comptage en cours…
              </p>
            ) : preview.requests === null ? (
              <p className="text-xs text-zinc-500 font-mono">—</p>
            ) : (
              <div className="text-xs text-zinc-700 font-mono">
                <span className="text-red-700 font-semibold">{preview.requests}</span> demande(s) ·{" "}
                <span className="text-red-700 font-semibold">{preview.assignments}</span> affectation(s)
                {nothingToPurge && (
                  <span className="ml-2 text-emerald-700">— rien à supprimer</span>
                )}
              </div>
            )}
            {exported && (
              <p className="text-[11px] text-emerald-700 font-mono">
                ✓ CSV téléchargé, vous pouvez purger en toute sécurité.
              </p>
            )}
          </div>

          <Alert className="border-amber-300 bg-amber-50 text-amber-900">
            <AlertTriangle size={16} className="text-amber-700" />
            <AlertTitle className="text-amber-900">Action irréversible</AlertTitle>
            <AlertDescription className="text-amber-800 text-xs">
              Les demandes ET les affectations correspondantes seront supprimées.
              Une trace sera conservée dans <span className="font-mono">audit_logs</span>.
            </AlertDescription>
          </Alert>

          <div className="space-y-1">
            <Label htmlFor="purge-confirm" className="text-xs">
              Tapez <span className="font-mono font-bold text-red-700">PURGE</span> pour confirmer
            </Label>
            <Input
              id="purge-confirm"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder="PURGE"
              autoComplete="off"
              data-testid="purge-confirm-input"
            />
          </div>

          {rangeInvalid && (
            <p className="text-xs text-red-600 font-medium">
              La date de début doit être antérieure ou égale à la date de fin.
            </p>
          )}
        </div>

        <DialogFooter className="gap-2 mt-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange?.(false)}
            disabled={busy}
            data-testid="purge-cancel-btn"
          >
            Annuler
          </Button>
          <Button
            onClick={submit}
            disabled={busy || !isConfirmed || rangeInvalid}
            className="bg-red-600 hover:bg-red-700 text-white"
            data-testid="purge-submit-btn"
          >
            {busy ? (
              <>
                <Loader2 size={14} className="mr-1.5 animate-spin" /> Purge en cours…
              </>
            ) : (
              "Purger maintenant"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
